import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import {
  CloudSun,
  Mountains,
  Wind,
  ChartLineUp,
  Globe,
  Bell,
} from "@phosphor-icons/react";
import { useContent } from "@/contexts/ContentContext";

// Icon registry — admins reference these names in CMS
const ICON_MAP = {
  CloudSun,
  Mountains,
  Wind,
  ChartLineUp,
  Globe,
  Bell,
};

// Visual style overrides applied by index for the bento grid
const FEATURE_STYLES = [
  {
    span: "col-span-12 md:col-span-7",
    accent: "text-sky-600",
    accentBg: "bg-sky-50",
    image: "https://images.unsplash.com/photo-1522995387705-94ec8dc52e80?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxuYXR1cmUlMjB3ZWF0aGVyJTIwc2t5JTIwY2xvdWRzJTIwbGFuZHNjYXBlfGVufDB8fHx8MTc3NTk4MTY4MHww&ixlib=rb-4.1.0&q=85",
  },
  {
    span: "col-span-12 md:col-span-5",
    accent: "text-orange-700",
    accentBg: "bg-orange-50",
    image: "https://images.unsplash.com/photo-1749518164057-03ab2ddf9b0f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHxzZWlzbWljJTIwZWFydGhxdWFrZSUyMHRlcnJhaW4lMjBsYW5kc2NhcGV8ZW58MHx8fHwxNzc1OTgxNjgwfDA&ixlib=rb-4.1.0&q=85",
  },
  {
    span: "col-span-12 md:col-span-5",
    accent: "text-emerald-700",
    accentBg: "bg-emerald-50",
    image: "https://images.unsplash.com/photo-1770557633047-860f848da3a6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwzfHxjbGVhbiUyMGFpciUyMHF1YWxpdHklMjB3aW5kJTIwZm9yZXN0JTIwbGFuZHNjYXBlfGVufDB8fHx8MTc3NTk4MTY4MHww&ixlib=rb-4.1.0&q=85",
  },
  {
    span: "col-span-12 md:col-span-7",
    accent: "text-violet-600",
    accentBg: "bg-violet-50",
    noImage: true,
  },
];

function FeatureCard({ feature, style, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const Icon = ICON_MAP[feature.icon] || ChartLineUp;
  const image = style?.image && !style?.noImage ? style.image : null;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className={`${style?.span || "col-span-12 md:col-span-6"} group`}
      data-testid={`feature-card-${(feature.title || "").toLowerCase().replace(/\s+/g, "-")}`}
    >
      <Link to={feature.href || "/dashboard"} className="block h-full">
        <div className="relative h-full min-h-[280px] md:min-h-[340px] rounded-3xl overflow-hidden border border-stone-200 bg-white hover:shadow-xl transition-all duration-500 hover:-translate-y-1 cursor-pointer">
          {image && (
            <img
              src={image}
              alt={feature.title}
              className="absolute inset-0 w-full h-full object-cover opacity-20 group-hover:opacity-30 transition-opacity duration-500"
              loading="lazy"
            />
          )}
          <div className="relative z-10 p-8 lg:p-10 flex flex-col justify-end h-full">
            <div className={`w-12 h-12 rounded-2xl ${style?.accentBg || "bg-emerald-50"} flex items-center justify-center mb-5`}>
              <Icon size={24} weight="duotone" className={style?.accent || "text-emerald-700"} />
            </div>
            <h3 className="font-heading text-xl sm:text-2xl font-medium text-stone-900 mb-3">
              {feature.title}
            </h3>
            <p className="font-body text-sm lg:text-base text-stone-500 leading-relaxed max-w-sm">
              {feature.description}
            </p>
            <span className="mt-3 text-xs font-medium text-emerald-700 font-body">Explore data →</span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export default function FeaturesSection() {
  const headerRef = useRef(null);
  const headerInView = useInView(headerRef, { once: true, margin: "-80px" });
  const { content } = useContent();
  const cms = content?.features || {};
  const items = Array.isArray(cms.items) ? cms.items : [];
  const miniItems = Array.isArray(cms.mini_items) ? cms.mini_items : [];
  const label = cms.label || "Monitoring Capabilities";
  const titleLines = (cms.title || "Comprehensive Earth\nData at Your Fingertips").split("\n");

  return (
    <section
      id="features"
      data-testid="features-section"
      className="relative py-16 sm:py-24 lg:py-32 bg-stone-50"
    >
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: `url("https://static.prod-images.emergentagent.com/jobs/c8b3d0a9-5937-480a-8c5c-11fe0e5f5b28/images/27730ba4de856ca229e3a050a743fce162faf72990fc56b4f3e88fff5f388e8d.png")`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={headerRef}
          initial={{ opacity: 0, y: 30 }}
          animate={headerInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="mb-16 lg:mb-20"
        >
          <p className="text-xs tracking-[0.25em] uppercase font-semibold text-emerald-700 mb-3 font-body" data-testid="features-label">
            {label}
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-stone-900 max-w-lg" data-testid="features-title">
            {titleLines.map((l, i) => (
              <span key={i}>
                {l}
                {i < titleLines.length - 1 && <br />}
              </span>
            ))}
          </h2>
        </motion.div>

        <div className="grid grid-cols-12 gap-6 lg:gap-8">
          {items.map((feature, index) => (
            <FeatureCard
              key={`${feature.title}-${index}`}
              feature={feature}
              style={FEATURE_STYLES[index % FEATURE_STYLES.length]}
              index={index}
            />
          ))}
        </div>

        <div className="mt-16 lg:mt-20 grid grid-cols-1 md:grid-cols-3 gap-6">
          {miniItems.map((item, index) => {
            const Icon = ICON_MAP[item.icon] || Globe;
            return (
              <motion.div
                key={`${item.title}-${index}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex items-start gap-4 p-6 rounded-2xl bg-white border border-stone-200 hover:shadow-md transition-all duration-300"
                data-testid={`mini-feature-${(item.title || "").toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Link to={item.href || "/dashboard"} className="flex items-start gap-4 w-full">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <Icon size={20} weight="duotone" className="text-emerald-700" />
                  </div>
                  <div>
                    <h4 className="font-heading text-sm font-semibold text-stone-900 mb-1">
                      {item.title}
                    </h4>
                    <p className="font-body text-sm text-stone-500 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
